/**
 * Trace ID utilities for distributed request traceability.
 *
 * Every request originating from the frontend attaches a fresh `X-Trace-Id` header.
 * The backend services (federated-api → federated-ai) propagate this header
 * through the call chain so that a single trace ID appears in logs across all
 * services touched by that request.
 *
 * Usage:
 *   import { makeTraceHeaders } from '@/lib/traceId';
 *
 *   // Included automatically via the Axios interceptor (lib/api.ts)
 *   // and authenticatedFetch (lib/auth.ts). Use directly for custom fetch calls.
 *   const headers = makeTraceHeaders();
 *   await fetch(url, { headers: { ...headers, Authorization: `Bearer ${token}` } });
 */

const TRACE_ID_HEADER = "X-Trace-Id" as const;

/**
 * Generate a UUID v4 string.
 *
 * Uses `crypto.randomUUID()` when available (all modern browsers and Node ≥ 15).
 * Falls back to a manual Math.random() implementation for older environments.
 */
function generateUUID(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return (crypto as Crypto).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate a new trace ID.
 *
 * Each call returns a distinct UUID — use `makeTraceHeaders()` at request time
 * so every HTTP call gets its own trace ID.
 */
export function getTraceId(): string {
  return generateUUID();
}

/** @deprecated Each request now gets a fresh ID automatically; this is equivalent to `getTraceId()`. */
export function refreshTraceId(): string {
  return generateUUID();
}

/**
 * Return a headers object containing a new `X-Trace-Id` for this request.
 */
export function makeTraceHeaders(): Record<string, string> {
  return { [TRACE_ID_HEADER]: generateUUID() };
}

export { TRACE_ID_HEADER };
